/**
 * Le contrat de l’inscription en démonstration, vu du navigateur.
 *
 * Quatre appels, quatre moments du parcours. Aucun d’eux n’exige de session :
 * l’inscription se fait avant qu’un théâtre — donc un compte — existe.
 */

import { api } from '../../lib/api'

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type RaisonIndisponible = 'format' | 'reserve' | 'pris'

export interface Disponibilite {
  readonly slug: string
  readonly disponible: boolean
  readonly raison?: RaisonIndisponible
  /** Phrase écrite par le serveur, affichable telle quelle. */
  readonly message: string
  readonly suggestions: readonly string[]
}

export interface Accuse {
  readonly id: string | null
  readonly message: string
}

export type StatutDemande =
  'en_attente' | 'verifiee' | 'provisionnement' | 'provisionnee' | 'echec' | 'abandonnee'

export interface ResultatVerification {
  readonly demandeId: string
  readonly slug: string
  readonly statut: StatutDemande
}

/** ★ EVT — la nature de la demande : le montage n’est pas le même. */
export type TypeDemande = 'demo' | 'evenement'

/**
 * ★ EVT-02 — la description d’un événement ponctuel, **entière ou absente**.
 *
 * C’est sa présence qui fait de la demande un `evenement` plutôt qu’une
 * `demo` : le serveur en déduit le type, et le `CHECK` de la migration 0017
 * refuse en base tout état intermédiaire. Un drapeau séparé aurait laissé
 * exister « type événement, pas de date ».
 */
export interface EvenementDemande {
  /** Date et heure de la soirée, en ISO 8601 avec décalage. */
  readonly date: string
  readonly lieuNom: string
  readonly jauge: number
  /** Prix d’une entrée, en centimes entiers (ARCHITECTURE §3). */
  readonly tarifCentimes: number
}

export interface EtatDemande {
  readonly id: string
  readonly statut: StatutDemande
  readonly slug: string
  readonly nomTheatre: string
  /** ★ EVT — `demo` ou `evenement` : l’écran d’attente ne dit pas la même chose. */
  readonly type: TypeDemande
  readonly message: string
  /** Adresse du théâtre — renseignée seulement une fois qu’il répond. */
  readonly url: string | null
  /** Le courriel contenant le lien et les identifiants a été remis au transport SMTP. */
  readonly accesEnvoyes: boolean
  readonly termine: boolean
}

/* -------------------------------------------------------------------------- */
/* Appels                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Le libre-service accepte-t-il des demandes sur cet environnement ?
 *
 * Fermé, le formulaire ne se contente pas d'être inutile : il promet une
 * adresse `<slug>.<domaine>` que l'environnement ne sait pas servir, et le
 * prospect s'en aperçoit en cliquant sur le premier lien qu'il reçoit de nous.
 * Autant ne pas ouvrir la porte.
 */
export function lireInscriptionOuverte(): Promise<{ ouverte: boolean }> {
  return api.get<{ ouverte: boolean }>('/inscription/ouverte')
}

export function verifierDisponibilite(slug: string, signal?: AbortSignal): Promise<Disponibilite> {
  return api.get<Disponibilite>('/inscription/disponibilite', {
    query: { slug },
    ...(signal === undefined ? {} : { signal }),
  })
}

export function deposerDemande(corps: {
  readonly nomTheatre: string
  readonly prenom: string
  readonly nom: string
  readonly email: string
  readonly slug: string
  readonly cguAcceptees: true
  /** Pot de miel : un navigateur humain laisse toujours ce champ vide. */
  readonly website?: string
  /** ★ EVT-02 — absent : une démonstration. Présent : un événement ponctuel. */
  readonly evenement?: EvenementDemande
}): Promise<Accuse> {
  return api.post<Accuse>('/inscription', corps)
}

export function confirmerAdresse(jeton: string): Promise<ResultatVerification> {
  return api.post<ResultatVerification>('/inscription/verification', { jeton })
}

export function lireEtat(id: string, signal?: AbortSignal): Promise<EtatDemande> {
  return api.get<EtatDemande>(`/inscription/${id}`, signal === undefined ? {} : { signal })
}

/**
 * ★ Renvoyer le lien de vérification.
 *
 * Aucun paramètre en dehors de l'identifiant de demande : le serveur écrit à
 * l'adresse ENREGISTRÉE, jamais à une adresse fournie ici. C'est la garantie
 * qui empêche cette route de devenir un moyen d'expédier du courrier signé du
 * théâtre à n'importe qui.
 */
export function renvoyerVerification(id: string): Promise<{ id: string; message: string }> {
  return api.post<{ id: string; message: string }>(`/inscription/${id}/renvoyer`, {})
}
